import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from 'componentsV2/Button';
import { useSelector } from 'react-redux';
import { DEVICE_IOS, DEVICE_ANDROID, mobileDeviceType } from 'helpers/ContentHelper';
import { getCookie, removeCookie } from 'helpers/Storage';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import { openApp, successModalProccessor } from './SuccessModalHelper';
import Modal from '../Modal';
import style from './SuccessModal.module.scss';

const FOX_LOGO = `https://static-media.fox.com/foxnow/web/v3-2/img/network/fox-nation-square.png`;
const FN_DEVICES = `/static/images/FNDevices.png`;
const NATCOM_SIGNUP_COMPLETED = 'NATCOM_SIGNUP_COMPLETED';

const SuccessModal = ({ accessToken = '', deviceId = '', disableScroll, theme = {} }) => {
  const { profile } = useSelector(state => state?.app);
  const [showModal, setShowModal] = useState(false);
  const deviceType = mobileDeviceType();
  const isMobile = deviceType === DEVICE_IOS || deviceType === DEVICE_ANDROID;
  const { headline, description, downloadAppCTA, watchCTA } = useMemo(
    () => successModalProccessor(isMobile),
    [isMobile],
  );

  const onClickCloseModal = () => {
    setShowModal(false);
    removeCookie({ key: NATCOM_SIGNUP_COMPLETED, options: { path: '/' } });
    disableScroll({ disableScroll: false, bodyOnly: false });
  };

  const onClickDownloadApp = async () => {
    try {
      if (deviceId && accessToken) {
        const { code: regCode = '' } = await profile?.regCodeDeeplink({ deviceId });
        if (regCode) {
          handleAnalytics({
            event: Events.BUTTON_LINK_SELECTED,
            payload: {
              page_collection_title: headline,
              page_button_text: downloadAppCTA,
              page_modal_type: 'modal',
              page_name: 'delta:web:featured:landing',
              page_type: 'featured:landing',
            },
          });
          onClickCloseModal();
          openApp(regCode);
        }
      }
    } catch (error) {
      console.error(error);
      onClickCloseModal();
    }
  };

  const onClickWatchCTA = () => {
    handleAnalytics({
      event: Events.BUTTON_LINK_SELECTED,
      payload: {
        page_collection_title: headline,
        page_button_text: watchCTA,
        page_modal_type: 'modal',
        page_name: 'delta:web:featured:landing',
        page_type: 'featured:landing',
      },
    });
    onClickCloseModal();
  };

  useEffect(() => {
    const isSignUpCompleted = getCookie({ name: NATCOM_SIGNUP_COMPLETED });
    if (isSignUpCompleted) {
      setShowModal(true);
      disableScroll({ disableScroll: true, bodyOnly: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      showModal={showModal}
      onClickCloseModal={onClickCloseModal}
      theme={{ modalContent: style.modalContent, closeIconContainer: style.closeIconContainer }}
      closeIconColor="white"
    >
      <div className={classNames(style.modalBackground, theme.modalBackground)}>
        <div className={classNames(style.modalInnerContainer, theme.modalInnerContainer)}>
          <img
            src={FOX_LOGO}
            className={classNames(style.modalLogo, theme.modalLogo)}
            alt="fox logo"
          />
          <h3 className={classNames(style.modalHeadline, theme.modalHeadline)}>{headline}</h3>
          <p className={classNames(style.modalDesc, theme.modalDesc)}>{description}</p>
        </div>
      </div>
      <div className={classNames(style.modalBody, theme.modalBody)}>
        <img
          src={FN_DEVICES}
          className={classNames(style.modalBodyImg, theme.modalBodyImg)}
          alt="devices"
        />
        <div className={classNames(style.ctaContainer, theme.ctaContainer)}>
          {isMobile && <Button label={downloadAppCTA} onClick={onClickDownloadApp} />}
          <Button label={watchCTA} onClick={onClickWatchCTA} />
        </div>
      </div>
    </Modal>
  );
};

SuccessModal.propTypes = {
  theme: PropTypes.shape({}),
  accessToken: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  disableScroll: PropTypes.func.isRequired,
};

export default SuccessModal;
